<template>
	<div class="shopping-cart-container">
		<div :id="elementId" class="scroll-reference" />

		<div class="shopping-cart-week-container">
			<shoppingcart-week-summary :week="week"
			                           :year="year"
			                           :is-active-week="isActiveWeek"
			                           :order-lines="orderLines"
			                           :show-invalid-order-line-warning="weekContainsInvalidOrderLines"
			                           @on-close-shopping-cart="closeShoppingCart"
			/>

			<button type="button"
			        class="button button--white button--small show-order-lines-button"
			        @click="toggleOrderLines">
				{{ showOrderLines ? t('shoppingcart.hideProducts.text') : t('shoppingcart.showProducts.text') }}

				<svg :class="{'active': showOrderLines}"
				     class="icon-chevron-bottom"
				     height="8"
				     width="12">
					<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#chevron-bottom"></use>
				</svg>
			</button>
		</div>

		<Transition name="fade">
			<div v-if="showOrderLines" class="shopping-cart-order-lines">
				<div class="table-wrapper mydummen-table-wrapper  mydummen-table-wrapper--order-detail">
					<shoppingcart-order-lines
							:order-lines="orderLinesGroupedByConsignment"
							:show-orders-with-availability-issues="showOrdersWithAvailabilityIssues"
					/>
				</div>
			</div>
		</Transition>
	</div>
</template>

<script lang="ts" setup>
	import ShoppingcartOrderLines from '@/components/ShoppingcartOrderLines.vue';
	import ShoppingcartWeekSummary from '@/components/ShoppingcartWeekSummary.vue';
	import {groupAndSortByConsignment} from '@/service/order.line.service';
	import {computed, ref} from 'vue';
	import {type ShoppingCartOrderLine} from 'dummen-shared/src/interfaces/order.line.type.ts';
	import {useShoppingCartStore} from '@/store/shoppingcart.store.ts';
	import {isInvalidAmount} from 'dummen-shared';
	import {useI18n} from 'vue-i18n';

	const {t} = useI18n();

	const emit = defineEmits(['close-shopping-cart']);

	const props = defineProps<{
		year: number;
		week: number;
		orderLines: ShoppingCartOrderLine[];
		showOrdersWithAvailabilityIssues: boolean;
	}>();

	const elementId = `week-${props.week}-${props.year}`;

	const shoppingCartStore = useShoppingCartStore();

	const orderLinesToggled = ref(true);

	const isActiveWeek = computed(() => `${props.week}-${props.year}` === shoppingCartStore.activeWeek);

	const showOrderLines = computed(() => {
		if (shoppingCartStore.collapseWeeks) {
			return isActiveWeek.value;
		}

		return orderLinesToggled.value;
	});

	const orderLinesGroupedByConsignment = computed(() => groupAndSortByConsignment(props.orderLines));

	const scrollToActiveWeek = () => {
		setTimeout(function () {
			const weekElement: HTMLElement | null = document.getElementById(elementId);
			weekElement?.scrollIntoView({behavior: 'instant', block: 'start', inline: 'nearest'});
		}, 0); // Timeout of 0 to solve race condition.
	}

	const setActiveWeek = () => {
		if (isActiveWeek.value) {
			shoppingCartStore.setActiveWeek(null);
			return;
		}

		shoppingCartStore.setActiveWeek({week: props.week, year: props.year});

		// Only scroll if there are many order lines to prevent too much jumping around.
		if (orderLinesToggled.value && props.orderLines.length > 10) {
			scrollToActiveWeek();
		}
	};

	const toggleOrderLines = () => {
		orderLinesToggled.value = !orderLinesToggled.value;
		setActiveWeek();
	};

	const weekContainsInvalidOrderLines = computed(() => {
		return props.orderLines.some(line => {
			return isInvalidAmount(
				line.amount,
				line.packingUnit,
				line.amountAvailable,
				line.allowOverbooking
			)
		});
	});

	const closeShoppingCart = () => emit('close-shopping-cart');

</script>

<style lang="scss" scoped>
	.shopping-cart-container {
		width: 100%;
		max-width: 1200px;
		margin: auto auto 75px;

		// Ensure that the scroll position is correct when navigating to a week.
		.scroll-reference {
			visibility: hidden;
			scroll-margin-top: 50px;
		}

		.shopping-cart-week-container {
			text-align: center;
			background: white;
			position: sticky;
			top: 42px;
			z-index: 1;
		}

		.fade-enter-active,
		.fade-leave-active {
			transition: opacity 0.5s ease;
		}

		.fade-enter-from,
		.fade-leave-to {
			opacity: 0;
		}
	}

	.show-order-lines-button {
		width: 100%;
		font-size: .75em;

		.icon-chevron-bottom {
			display: block;
			margin: 5px auto auto;

			&.active {
				transform: rotate(-180deg);
			}
		}
	}
</style>
